import React from "react";
import {useInjection} from "inversify-react";
import {observer} from "mobx-react";
import {AppController} from "@dropDesk/presentation/app/app.controller";
import {PlanExtensionCatalogEntity} from "@dropDesk/domain/entities/plan_extensions/plan_extension_catalog.entity";
import {
    CardAction,
    CardBadge,
    CardDescription,
    CardDescriptionRow,
    CardDivider,
    CardIconWrapper,
    CardInfo,
    CardName,
    CardNameRow,
    CardPrice,
    CardTop,
    CardWrapper,
    SaibaMaisLink,
    StoreLink,
    TrialButtons,
    TrialDescription,
    TrialIconWrapper,
    TrialSubcard,
    TrialTextGroup,
    TrialTitle,
    TrialTitleRow,
} from "./styled";
import DropDeskIcon from "@dropDesk/storage/icons";
import {ICONS_DROPDESK} from "@dropDesk/storage/icons/icons.d";
import ExtensionBenefits from "./extension_benefits";
import ExtensionTrialCard from "./extension_trial_card";
import ButtonMain from "@dropDesk/presentation/components/buttons/button_main";
import {SharedColors} from "@dropDesk/domain/entities/theme/app_colors";

const LP_URL_BY_SLUG: Record<string, string> = {
    'internal_chat': process.env.LP_URL_CHAT_INTERNO ?? '',
    'client_panel': process.env.LP_URL_PORTAL_CLIENTE ?? '',
    'use_app': process.env.LP_URL_APLICATIVO ?? '',
};

const AppleIcon = () => (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
);

const AndroidIcon = () => (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor">
        <path d="M3.18 23.76c.34.19.72.24 1.09.14l12.48-7.2-2.7-2.7-10.87 9.76zm-1.61-19.7C1.22 4.47 1 5.01 1 5.69v12.62c0 .68.22 1.22.57 1.63l.09.08 7.07-7.07v-.17L1.66 5.7l-.09.36zm17.5 8.24l-2.13-1.23-2.44 2.44 2.44 2.44 2.14-1.24c.61-.35.61-.92 0-1.41zM4.27.48L16.75 7.68l-2.7 2.7L3.18.62C3.52.28 4 .28 4.27.48z"/>
    </svg>
);

type Props = {
    extension: PlanExtensionCatalogEntity;
    onAction: (extension: PlanExtensionCatalogEntity) => void;
    onCancel?: (extension: PlanExtensionCatalogEntity) => void;
    onReactivate?: (extension: PlanExtensionCatalogEntity) => void;
    onPay?: (extension: PlanExtensionCatalogEntity) => void;
    onCancelActivation?: (extension: PlanExtensionCatalogEntity) => void;
    subscriptionCanceledScheduled?: boolean;
    isTrialAccount?: boolean;
    onSubscribe?: () => void;
}

const ExtensionCard = observer(({extension, onAction, onCancel, onReactivate, onPay, onCancelActivation, subscriptionCanceledScheduled, isTrialAccount, onSubscribe}: Props) => {
    const appController = useInjection(AppController);
    const colors = appController.theme.colorScheme;

    const installed = extension.installed;
    const isEffectivelyInstalled = !!installed && !installed.isCanceled;
    const hasTrial = !isEffectivelyInstalled && !extension.isCore && !!extension.trialDays && extension.trialDays > 0;
    const isFreeFromAccountMigratedOrCore = extension.isCore || installed?.isFreeFromAccountMigrated;
    const landingPageUrl = LP_URL_BY_SLUG[extension.slug] ?? '';
    const isAppExtension = extension.slug === 'use_app';

    const badgeProps = (() => {
        if (isFreeFromAccountMigratedOrCore) {
            return {background: `${colors.success}18`, color: colors.success, label: 'Grátis', icon: null};
        }
        if (installed?.isActive) {
            return {background: `${colors.success}18`, color: colors.success, label: 'Ativa', icon: null};
        }
        if (installed?.isScheduledCancel) {
            return {background: `${colors.textError}18`, color: colors.textError, label: 'Cancelamento agendado', icon: null};
        }
        if (installed?.isWaitingPaymentOrPaymentRequired) {
            return {background: `${colors.warning}18`, color: colors.warning, label: 'Aguardando pagamento', icon: null};
        }
        return {background: `${colors.accent}18`, color: colors.accent, label: 'Premium', icon: ICONS_DROPDESK.subscription};
    })();

    const renderWaitingPaymentSubcard = () => (
        <TrialSubcard background={`${colors.warning}10`} border={`${colors.warning}30`}>
            <TrialTitleRow>
                <TrialIconWrapper background={`${colors.warning}25`} color={colors.warning}>
                    <DropDeskIcon icon={ICONS_DROPDESK.warning} size={14} color={colors.warning}/>
                </TrialIconWrapper>
                <TrialTextGroup>
                    <TrialTitle color={colors.text}>Pagamento pendente</TrialTitle>
                    <TrialDescription color={colors.hint}>
                        Realize o pagamento para ativar esta extensão.
                    </TrialDescription>
                </TrialTextGroup>
            </TrialTitleRow>
        </TrialSubcard>
    );

    const renderAction = () => {
        if (subscriptionCanceledScheduled) {
            return (
                <ButtonMain
                    widthPercentage={100}
                    letterFontSize={13}
                    height={38}
                    loading={false}
                    disabled={true}
                    color={'transparent'}
                    borderColor={colors.border}
                    letterColor={colors.hint}
                    type="button"
                    letter="Assinatura cancelada"
                    onClick={() => {}}
                />
            );
        }

        if (isTrialAccount && !isFreeFromAccountMigratedOrCore && !isEffectivelyInstalled) {
            return (
                <>
                    <TrialSubcard background={`${colors.accent}08`} border={`${colors.accent}25`}>
                        <TrialTitleRow>
                            <TrialIconWrapper background={`${colors.accent}20`} color={colors.accent}>
                                <DropDeskIcon icon={ICONS_DROPDESK.subscription} size={14} color={colors.accent}/>
                            </TrialIconWrapper>
                            <TrialTextGroup>
                                <TrialTitle color={colors.text}>Plano trial ativo</TrialTitle>
                                <TrialDescription color={colors.hint}>
                                    Assine um plano premium para ativar esta extensão.
                                </TrialDescription>
                            </TrialTextGroup>
                        </TrialTitleRow>
                    </TrialSubcard>
                    <ButtonMain
                        widthPercentage={100}
                        letterFontSize={13}
                        height={38}
                        loading={false}
                        color={colors.accent}
                        type="button"
                        letter="Assine um plano premium"
                        onClick={() => onSubscribe?.()}
                    />
                </>
            );
        }

        if (isFreeFromAccountMigratedOrCore) {
            return (
                <ButtonMain
                    widthPercentage={100}
                    letterFontSize={13}
                    height={38}
                    loading={false}
                    disabled={true}
                    color={'transparent'}
                    borderColor={colors.border}
                    letterColor={colors.hint}
                    type="button"
                    letter="Incluso no plano"
                    onClick={() => {}}
                />
            );
        }

        if (installed?.isWaitingPaymentOrPaymentRequired) {
            return (
                <>
                    {renderWaitingPaymentSubcard()}
                    <TrialButtons>
                        <ButtonMain
                            widthPercentage={100}
                            letterFontSize={13}
                            height={38}
                            loading={false}
                            color={colors.accent}
                            type="button"
                            letter="Pagar agora"
                            onClick={() => onPay?.(extension)}
                        />
                        <ButtonMain
                            widthPercentage={100}
                            letterFontSize={13}
                            height={38}
                            loading={false}
                            color={colors.textError}
                            letterColor={SharedColors.white}
                            borderColor={colors.textError}
                            type="button"
                            letter="Cancelar ativação"
                            onClick={() => onCancelActivation?.(extension)}
                        />
                    </TrialButtons>
                </>
            );
        }

        if (installed?.isScheduledCancel) {
            return (
                <ButtonMain
                    widthPercentage={100}
                    letterFontSize={13}
                    height={38}
                    loading={false}
                    color={colors.accent}
                    type="button"
                    letter="Reativar extensão"
                    onClick={() => onReactivate?.(extension)}
                />
            );
        }

        if ((installed?.isActive || installed?.isCanceled) && installed.isCancelable) {
            return (
                <ButtonMain
                    widthPercentage={100}
                    letterFontSize={13}
                    height={38}
                    loading={false}
                    color={colors.textError}
                    letterColor={SharedColors.white}
                    type="button"
                    letter="Cancelar"
                    onClick={() => onCancel?.(extension)}
                    borderColor={colors.textError}
                />
            );
        }

        return (
            <>
                {hasTrial &&
                    <ExtensionTrialCard
                        trialDays={extension.trialDays!}
                        onTrial={() => onAction(extension)}
                        onViewPlans={() => onAction(extension)}
                    />
                }
                <ButtonMain
                    widthPercentage={100}
                    letterFontSize={13}
                    height={38}
                    loading={false}
                    color={colors.accent}
                    type="button"
                    letter={hasTrial ? "Ativar grátis" : "Ativar"}
                    onClick={() => onAction(extension)}
                />
            </>
        );
    };

    return (
        <CardWrapper background={colors.onBackground} border={colors.border}>
            <CardTop>
                <CardIconWrapper
                    background={`${colors.accent}12`}
                    color={colors.accent}
                    dangerouslySetInnerHTML={{__html: extension.icon}}
                />
                <CardBadge background={badgeProps.background} color={badgeProps.color}>
                    {badgeProps.icon && (
                        <DropDeskIcon icon={badgeProps.icon} size={11} color={badgeProps.color}/>
                    )}
                    {badgeProps.label}
                </CardBadge>
            </CardTop>

            <CardInfo>
                <CardNameRow>
                    <CardName color={colors.text}>{extension.name}</CardName>
                    <CardPrice color={colors.text}>
                        {isFreeFromAccountMigratedOrCore
                            ? ''
                            : `R$ ${extension.defaultPrice.toLocaleString('pt-BR', {minimumFractionDigits: 2})}/mês`
                        }
                    </CardPrice>
                </CardNameRow>
                {extension.description && (
                    <CardDescription color={colors.hint}>{extension.description}</CardDescription>
                )}
                {(!!landingPageUrl || isAppExtension) && (
                    <CardDescriptionRow>
                        {/*{!!landingPageUrl && (*/}
                        {/*    <SaibaMaisLink*/}
                        {/*        color={colors.textLink}*/}
                        {/*        href={landingPageUrl}*/}
                        {/*        target="_blank"*/}
                        {/*        rel="noopener noreferrer"*/}
                        {/*    >*/}
                        {/*        Saiba mais*/}
                        {/*    </SaibaMaisLink>*/}
                        {/*)}*/}
                        {isAppExtension && (
                            <>
                                <StoreLink
                                    href={process.env.APP_URL_IOS ?? '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    background={`${colors.accent}15`}
                                    color={colors.accent}
                                >
                                    <AppleIcon/>
                                    iPhone
                                </StoreLink>
                                <StoreLink
                                    href={process.env.APP_URL_ANDROID ?? '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    background={`${colors.accent}15`}
                                    color={colors.accent}
                                >
                                    <AndroidIcon/>
                                    Android
                                </StoreLink>
                            </>
                        )}
                    </CardDescriptionRow>
                )}
            </CardInfo>

            <CardDivider color={colors.border}/>

            <ExtensionBenefits features={extension.features}/>

            <CardAction>{renderAction()}</CardAction>
        </CardWrapper>
    );
});

export default ExtensionCard;
